import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addOrRemoveColumns } from '../../reducers/tableColumnsSlice';
import type { IGenericTable } from '../types';
import {
  getSearchParams,
  restoreFilterColumnsFromLocalStorage,
  updateSearchParams,
} from '../utils';
import {
  FarmOptionsTypes,
  Tables,
} from '../../components/CustomizeColumns/types';
import {
  getBasicsDefaultFilters,
  getBasicsDefaultFiltersForAMF,
  getVectorSettingsBasicFilters,
} from '../../containers/SettingsPage/utils';
import { getBasicsDefaultFiltersForFarmsList } from '../../components/FarmsTable/utils';
import { CUSTOMIZABLE_HUB_DEVICE_COLUMNS } from '../../containers/HubDevicesPage/constants';

// Helper function to convert Hub Device columns to Selection format
const getBasicsDefaultFiltersForHubDevices =
  (): FarmOptionsTypes.Selection[] => {
    return CUSTOMIZABLE_HUB_DEVICE_COLUMNS.map((column) => ({
      key: column.key,
      name: column.title || '',
    }));
  };

// default filters for tables
const basicDefaultFilters: Record<
  string,
  Record<string, () => FarmOptionsTypes.Selection[]>
> = {
  [Tables.Settings]: { basics: getBasicsDefaultFilters },
  [Tables.AMFSettings]: { basics: getBasicsDefaultFiltersForAMF },
  [Tables.VectorSettings]: { basics: getVectorSettingsBasicFilters },
  [Tables.Farms]: {
    basics: getBasicsDefaultFiltersForFarmsList,
  },
  [Tables.HubDevices]: {
    basics: getBasicsDefaultFiltersForHubDevices,
  },
};

export const nullableFilters: Record<string, string[]> = {
  [Tables.Farms]: [],
};

const useFiltersRestore = (
  filterTitle: string,
  localStorageKey: string,
  tableName: string,
  data: FarmOptionsTypes.Selection[],
) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const isBackButtonNavigation = useRef<boolean>(false);
  const prevUrlSearch = useRef<string | null>(null);
  const prevLocation = useRef<string>('');

  useEffect(() => {
    if (prevLocation.current && prevLocation.current !== location.pathname) {
      // if location does not include filters in the search params, remove all filters
      if (!location.search.includes(filterTitle)) {
        dispatch(addOrRemoveColumns({ key: filterTitle, columns: [] }));
      }
    }
    prevLocation.current = location.pathname;
  }, [location]);

  useEffect(() => {
    if (!location.search.includes(filterTitle)) {
      dispatch(addOrRemoveColumns({ key: filterTitle, columns: [] }));
    }
    const urlFilters = getSearchParams(filterTitle);
    const localStorageFilters = localStorage.getObject<
      IGenericTable,
      typeof localStorageKey
    >(localStorageKey)?.filters;
    if (urlFilters && urlFilters !== 'null') {
      const urlFiltersArr = urlFilters.split(',');
      const presentValidFilters: string[] = [];

      const optionalFiltersKeysInUrl = data.filter((item) => {
        const isPresent = urlFiltersArr.includes(item.key);

        if (isPresent) {
          presentValidFilters.push(item.key);
        }

        return isPresent;
      });

      dispatch(
        addOrRemoveColumns({
          key: filterTitle,
          columns: optionalFiltersKeysInUrl,
        }),
      );

      // update local storage filters specific property
      localStorage.updateObjectPropertyKey<
        IGenericTable,
        typeof localStorageKey
      >(
        localStorageKey,
        'filters',
        filterTitle,
        optionalFiltersKeysInUrl.map((item) => item.key),
      );
    } else if (
      localStorageFilters &&
      localStorageFilters[filterTitle]?.length
    ) {
      if (isBackButtonNavigation.current) {
        dispatch(
          addOrRemoveColumns({
            key: filterTitle,
            columns: [],
          }),
        );

        localStorage.updateObjectPropertyKey<
          IGenericTable,
          typeof localStorageKey
        >(localStorageKey, 'filters', filterTitle, []);

        isBackButtonNavigation.current = false;
        return;
      }

      const restoredColumns = restoreFilterColumnsFromLocalStorage(
        localStorageFilters,
        tableName,
        data,
        filterTitle,
      );

      if (restoredColumns) {
        const { key, columns } = restoredColumns;
        dispatch(
          addOrRemoveColumns({
            key,
            columns,
          }),
        );
      }
      // pass this into dropdown selections and select default there instead of here
    } else if (basicDefaultFilters[tableName]?.[filterTitle]) {
      // For now done only for basic settings

      if (
        filterTitle === 'basics' &&
        localStorageFilters &&
        !localStorageFilters[filterTitle]?.length
      ) {
        return;
      }

      const basicFilters = basicDefaultFilters[tableName][filterTitle]();
      dispatch(
        addOrRemoveColumns({
          key: filterTitle,
          columns: basicFilters,
        }),
      );
      const filtersValuesKeys = basicFilters.map((item) => item.key);
      updateSearchParams(
        filterTitle,
        filtersValuesKeys.join(','),
        true,
        navigate,
      );
    } else {
      dispatch(
        addOrRemoveColumns({
          key: filterTitle,
          columns: [],
        }),
      );
    }

    prevUrlSearch.current = location.search;
  }, [location]);
};

export default useFiltersRestore;
